import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import _ from 'lodash';

import { getBrandTrackingHealth, getBrandTrackingHealthListStats } from '../../api/brand_tracking_health';

const DEBOUNCE_MS = 250;
const LIST_STATS_CHUNK_SIZE = 100; // the stats route reads and parses one healthData blob per brand, and caps a request here

// Union the account managers seen across fetches by id, so the filter dropdown stays populated from
// the (EDIT_BRAND-scoped) results themselves — no separate MANAGE_USERS-gated account-manager fetch —
// and doesn't collapse to one manager after a filtered fetch narrows the rows.
const mergeAccountManagers = (existing, brands) => {
  const byId = new Map(existing.map(accountManager => [accountManager.id, accountManager]));
  for (const brand of brands) brand.accountManager && !byId.has(brand.accountManager.id) && byId.set(brand.accountManager.id, brand.accountManager);
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const BrandTrackingHealthData = props => {
  const { curSearchVal, accountManagerId, setResults, setIsFetching, setAccountManagers, setBrandStats } = props;
  const latestRequest = useRef(0);
  const debouncedFetch = useRef();

  const fetchResults = async requestId => {
    try {
      const { brands } = await getBrandTrackingHealth([
        ['search', curSearchVal ?? ''],
        ['accountManagerId', accountManagerId ?? '']
      ]);
      if (requestId !== latestRequest.current) return; // a newer request superseded this one
      setResults(brands);
      setAccountManagers(prev => mergeAccountManagers(prev, brands));
      loadStats(brands, requestId); // lazy — a separate ~10MB healthData read that never blocks the list
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      console.error(error);
      cogoToast.error('There was an issue loading tracking health. Please try again.');
    }
    if (requestId === latestRequest.current) setIsFetching(false);
  };

  // Paged through the route's ceiling, painting each chunk as it lands so a broad search fills in
  // progressively instead of holding every brand's stats until the last page.
  const loadStats = async (brands, requestId) => {
    const stats = {};
    try {
      for (const brandIds of _.chunk(
        brands.map(brand => brand.Brand_id),
        LIST_STATS_CHUNK_SIZE
      )) {
        const response = await getBrandTrackingHealthListStats(brandIds);
        if (requestId !== latestRequest.current) return; // a newer request superseded this one
        Object.assign(stats, response.stats);
        setBrandStats({ ...stats });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Search is debounced; an account-manager change (a dropdown, not typing) fetches immediately.
  useEffect(() => {
    const requestId = ++latestRequest.current;
    setIsFetching(true);
    clearTimeout(debouncedFetch.current);
    debouncedFetch.current = setTimeout(() => fetchResults(requestId), curSearchVal ? DEBOUNCE_MS : 0);
    return () => clearTimeout(debouncedFetch.current);
  }, [curSearchVal, accountManagerId]);

  return null;
};

BrandTrackingHealthData.propTypes = {
  curSearchVal: PropTypes.string.isRequired,
  accountManagerId: PropTypes.string.isRequired,
  setResults: PropTypes.func.isRequired,
  setIsFetching: PropTypes.func.isRequired,
  setAccountManagers: PropTypes.func.isRequired,
  setBrandStats: PropTypes.func.isRequired
};

export default BrandTrackingHealthData;
